// Cours CODE 2 ROUES — Ma première circulation A2
// Contenu maître validé · Signé Bruno LLORENS · 2026
// Structure : 14 sections (Hero + 12 chapitres + Réflexes finaux) conformes au cahier des charges

export const coursData = {
  slug: 'premiere-circulation-a2',
  title: 'Ma première circulation A2',
  subtitle: '2 heures pour observer, comprendre et commencer à construire ta conduite',
  duration: '2 heures',
  author: 'Bruno LLORENS · CODE 2 ROUES',
  mantra: 'REGARDER → COMPRENDRE → ANTICIPER → DÉCIDER → AGIR',
  heroBody:
    "Tu quittes le plateau pour entrer dans la circulation réelle. Cette première séance permet à ton enseignant d'observer ta conduite, d'identifier tes acquis et tes besoins, puis de construire avec toi les premiers objectifs de progression.",

  sections: [
    {
      num: '01',
      kicker: 'Structure de la séance',
      title: 'Ta première séance : 2 heures pour apprendre',
      image: '/images/articles/cours-c1.png',
      imageAlt: 'Structure de la première séance de circulation A2 — Code 2 Roues',
      type: 'timeline',
      steps: [
        { num: '01', title: 'ÉCHANGER', body: 'Nous faisons le point sur tes connaissances.' },
        { num: '02', title: 'PRÉPARER', body: "Nous définissons l'organisation, les consignes et le cadre de sécurité." },
        { num: '03', title: 'OBSERVER', body: 'Tu conduis pendant que ton enseignant réalise ton évaluation dynamique.' },
        { num: '04', title: 'TRAVAILLER', body: 'Nous déterminons les objectifs prioritaires.' },
        { num: '05', title: 'PROGRESSER', body: 'Tu expérimentes, corriges et recommences.' },
        { num: '06', title: 'FAIRE LE BILAN', body: 'Nous déterminons tes acquis et ton prochain objectif.' },
      ],
      retenir: "Tu ne viens pas simplement faire 2 heures de moto. Tu viens apprendre à conduire de plus en plus seul.",
    },
    {
      num: '02',
      kicker: 'Avant de partir',
      title: 'Ton évaluation statique',
      image: '/images/articles/cours-c2.png',
      imageAlt: 'Évaluation statique avant départ — Code 2 Roues',
      lead: "L'enseignant échange avec toi pour déterminer :",
      diagram: 'CE QUE TU SAIS → CE QUE TU COMPRENDS → CE QUE NOUS DEVONS TRAVAILLER',
      list: ['Équipement', 'Commandes', 'Regard', 'Positionnement', 'Allure', 'Intersections', 'Giratoires', 'Sécurité'],
      retenir: "Ce n'est pas un examen. Cette première évaluation permet de construire ta séance.",
    },
    {
      num: '03',
      kicker: 'Organisation',
      title: 'Nous roulons en groupe',
      image: '/images/articles/cours-c3.png',
      imageAlt: 'Organisation du convoi en circulation — Code 2 Roues',
      type: 'convoy',
      convoy: [
        { icon: '🏍', label: 'LEADER' },
        { icon: '🏍', label: 'ÉLÈVE 2' },
        { icon: '🏍', label: 'ÉLÈVE 3' },
        { icon: '🚗', label: 'ENSEIGNANT' },
      ],
      body: [
        "La voiture de l'enseignant est <strong>toujours derrière le groupe de motos</strong>. La radio lui permet de guider, questionner et corriger sans jamais conduire à ta place.",
      ],
      splitMessage: {
        left: { color: '#cf2031', text: 'NOTRE OBJECTIF : RESTER EN GROUPE' },
        right: { color: '#f97316', text: "RESTER EN GROUPE NE JUSTIFIE JAMAIS DE PRENDRE UN RISQUE" },
      },
    },
    {
      num: '04',
      kicker: 'Ton rôle',
      title: 'Leader ou suiveur : ton rôle change',
      image: '/images/articles/cours-c4.png',
      imageAlt: 'Rôle de leader ou suiveur en groupe moto — Code 2 Roues',
      type: 'split',
      split: {
        left: {
          kicker: 'TU ES LEADER',
          points: ['TU OUVRES', 'TU ANTICIPES', 'TU RÉGULES', 'TU SURVEILLES LA COHÉSION'],
          note: "Le leader adapte notamment son allure afin de ne pas étirer inutilement le groupe.",
        },
        right: {
          kicker: 'TU ES SUIVEUR',
          points: ['TU OBSERVES', 'TU GARDES TON ESPACE', 'TU RESTES DANS LE GROUPE', 'TU PRENDS TES PROPRES DÉCISIONS'],
          note: "Tu regardes loin, pas seulement le feu arrière de la moto devant toi.",
        },
      },
      retenir: 'Tu suis le groupe. Tu ne copies jamais aveuglément la moto devant toi.',
    },
    {
      num: '05',
      kicker: 'Communication',
      title: 'Ton enseignant te guide par radio',
      image: '/images/articles/cours-c5.png',
      imageAlt: "L'enseignant guide l'élève par radio — Code 2 Roues",
      type: 'radioBlocks',
      radioBlocks: {
        heard: {
          kicker: "CE QUE DIT L'ENSEIGNANT",
          body: '« À la prochaine intersection, nous prenons à droite. »',
        },
        understood: {
          kicker: 'CE QUE TU DOIS COMPRENDRE',
          body: "Il m'indique une direction. Il ne me donne pas l'autorisation de passer.",
        },
      },
      diagram: "J'OBSERVE → JE CONTRÔLE → J'ADAPTE → JE DÉCIDE → J'AGIS",
    },
    {
      num: '06',
      kicker: 'Situation particulière',
      title: 'Plus de radio ? Pas de panique',
      image: '/images/articles/cours-c6.png',
      imageAlt: 'Que faire en cas de coupure radio — Code 2 Roues',
      type: 'doDont',
      doDont: {
        do: {
          kicker: 'À FAIRE',
          points: ['Tu restes calme.', 'Tu continues à conduire.', 'Tu respectes la signalisation.', 'Tu conserves un comportement prévisible.'],
        },
        dont: {
          kicker: 'À NE PAS FAIRE',
          points: ['Pas de freinage brutal.', 'Pas de changement soudain de direction.', 'Pas de demi-tour improvisé.'],
        },
      },
      retenir: 'PLUS DE RADIO ≠ PLUS DE CONDUITE.',
    },
    {
      num: '07',
      kicker: 'Incident',
      title: 'Panne, chute ou problème technique',
      image: '/images/articles/cours-c7.png',
      imageAlt: 'Panne, chute ou problème technique à moto — Code 2 Roues',
      type: 'steps3',
      steps: [
        { title: 'ANOMALIE', body: 'Tu détectes un comportement inhabituel.' },
        { title: 'RALENTISSEMENT', body: 'Tu conserves le contrôle et réduis progressivement ton allure.' },
        { title: 'MISE EN SÉCURITÉ', body: "Tu recherches un emplacement adapté pour t'immobiliser." },
      ],
      retenir: 'TA SÉCURITÉ PASSE AVANT LA MOTO.',
    },
    {
      num: '08',
      kicker: 'Observation',
      title: 'Ton évaluation dynamique',
      image: '/images/articles/cours-c8.png',
      imageAlt: 'Évaluation dynamique en circulation A2 — Code 2 Roues',
      lead: "Une fois en circulation, ton enseignant observe.",
      list: ['Regard', 'Allure', 'Positionnement', 'Distances', 'Contrôles', 'Intersections', 'Giratoires', 'Communication', 'Anticipation', 'Cohésion du groupe'],
      body: [
        "Ce n'est pas un examen. L'enseignant cherche à déterminer :",
      ],
      diagram: 'DE QUOI AS-TU BESOIN POUR PROGRESSER ?',
    },
    {
      num: '09',
      kicker: '3 fondamentaux',
      title: 'Les 3 piliers de ta conduite',
      image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_occasions-jallieu/artifacts/2mqaefrv_ChatGPT%20Image%2017%20ao%C3%BBt%202026%2C%2016_30_41.png',
      imageAlt: 'Infographie Code 2 Roues — Les distances de sécurité : observe, compte 2 secondes, adapte, anticipe',
      type: 'cards3',
      cards: [
        {
          kicker: 'TON REGARD',
          diagram: 'LOIN → PROCHE → CÔTÉS → RÉTROVISEURS → LOIN',
          body: 'Tu ne regardes pas seulement ce qui se passe. Tu recherches ce qui peut se passer.',
        },
        {
          kicker: 'TON POSITIONNEMENT',
          diagram: 'VOIR → ÊTRE VU → GARDER DE L\u2019ESPACE',
          body: 'Adapté à la visibilité, aux véhicules stationnés, aux piétons et aux intersections.',
        },
        {
          kicker: 'TON ALLURE',
          diagram: 'LIMITATION ≠ ALLURE À ADOPTER SYSTÉMATIQUEMENT',
          body: "L'allure dépend de : Visibilité · Trafic · Adhérence · Environnement · Danger · Capacités.",
        },
      ],
    },
    {
      num: '10',
      kicker: 'Méthode',
      title: 'Intersections & giratoires',
      image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_occasions-jallieu/artifacts/u1ufw8rd_ChatGPT%20Image%2017%20ao%C3%BBt%202026%2C%2016_17_55.png',
      imageAlt: 'Infographie Code 2 Roues — Le giratoire : approche, positionnement, communication, contrôle, sortie',
      type: 'methodDual',
      methodDual: {
        first: {
          kicker: 'INTERSECTION',
          diagram: 'DÉTECTE → ADAPTE → OBSERVE → DÉCIDE → AGIS',
          body: 'La moto devant toi passe ? Tu effectues quand même ta propre analyse.',
        },
        second: {
          kicker: 'GIRATOIRE',
          diagram: 'OBSERVER → SE PLACER → COMMUNIQUER → CONTRÔLER → SORTIR',
          body: 'Cas travaillés séparément : 1re sortie · sortie dans le prolongement · sortie à gauche · demi-tour. La signalisation reste toujours prioritaire.',
        },
      },
    },
    {
      num: '11',
      kicker: 'Progression',
      title: 'Ta séance devient un cours',
      image: '/images/articles/cours-c11.png',
      imageAlt: 'La séance devient un cours : observer, corriger, recommencer, autonomie — Code 2 Roues',
      type: 'timeline',
      steps: [
        { num: '→', title: 'TON ENSEIGNANT OBSERVE', body: '' },
        { num: '→', title: 'NOUS IDENTIFIONS UN BESOIN', body: '' },
        { num: '→', title: 'NOUS FIXONS UN OBJECTIF', body: '' },
        { num: '→', title: 'TU ESSAIES', body: '' },
        { num: '→', title: 'TU ANALYSES', body: '' },
        { num: '→', title: 'TU CORRIGES', body: '' },
        { num: '→', title: 'TU RECOMMENCES', body: '' },
        { num: '→', title: "L'AIDE DIMINUE", body: '' },
        { num: '★', title: 'TU DEVIENS AUTONOME', body: '' },
      ],
      retenir: "Exemple d'objectif du jour : adapter ton allure suffisamment tôt à l'approche d'une intersection.",
    },
    {
      num: '12',
      kicker: 'Liaison livret',
      title: 'Ce que tu rencontres devient un cours',
      image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_occasions-jallieu/artifacts/fwwqhx9c_ChatGPT%20Image%2017%20ao%C3%BBt%202026%2C%2016_21_25.png',
      imageAlt: 'Infographie Code 2 Roues — Les changements de voie : anticiper, contrôler, signaler, exécuter, contrôler à nouveau',
      type: 'themeCards',
      themeCards: [
        { icon: '🚛', title: 'POIDS LOURD', body: 'Angles morts · visibilité' },
        { icon: '🌧', title: 'CHAUSSÉE HUMIDE', body: 'Adhérence · allure' },
        { icon: '🚗', title: 'VÉHICULES STATIONNÉS', body: 'Portières · piétons masqués' },
        { icon: '✚', title: 'INTERSECTION', body: 'Priorité · observation · anticipation' },
        { icon: '↻', title: 'GIRATOIRE', body: 'Placement · contrôles · communication' },
        { icon: '🏍', title: 'AUTRES DEUX-ROUES', body: 'Partage de la route · comportement' },
      ],
      retenir: "Chaque situation rencontrée devient l'occasion d'aborder un thème du livret d'apprentissage.",
    },
    {
      num: '13',
      kicker: 'Fin de séance',
      title: 'Ton bilan',
      image: '/images/articles/cours-c13.png',
      imageAlt: 'Fin de séance : faire le bilan, analyser, planifier, progresser — Code 2 Roues',
      type: 'bilan',
      bilan: [
        { icon: '✓', title: 'CE QUE TU MAÎTRISES', color: '#16a34a' },
        { icon: '↗', title: 'CE QUE TU DOIS ENCORE TRAVAILLER', color: '#f97316' },
        { icon: '🎯', title: 'TON PROCHAIN OBJECTIF', color: '#cf2031' },
      ],
      autoEval: 'Auto-évaluation : 🙂 À l\u2019aise / 😐 En progression / 🎯 À travailler',
    },
    {
      num: '14',
      kicker: 'À emporter',
      title: 'Les 7 réflexes CODE 2 ROUES',
      image: '/images/articles/cours-c14.png',
      imageAlt: "Les 7 réflexes Code 2 Roues : regarder, se positionner, adapter, anticiper, communiquer, garder de la marge, rester concentré",
      type: 'reflex7',
      reflex: [
        { icon: '👁️', title: 'JE REGARDE', body: "Je recherche l'information." },
        { icon: '🧠', title: "J'ANTICIPE", body: 'Je cherche ce qui peut arriver.' },
        { icon: '🏍️', title: "J'ADAPTE", body: "J'ajuste mon allure et mon comportement." },
        { icon: '📍', title: 'JE ME POSITIONNE', body: 'Je cherche à voir, être vu et conserver de l\u2019espace.' },
        { icon: '↔️', title: 'JE COMMUNIQUE', body: 'Je rends mes intentions compréhensibles.' },
        { icon: '👥', title: 'JE PENSE AU GROUPE', body: 'Je participe à sa cohésion.' },
        { icon: '✅', title: 'JE DÉCIDE', body: 'Je prends mes propres décisions.' },
      ],
      finalWords: 'TU ES GUIDÉ PAR TON ENSEIGNANT.\nTU PROGRESSES AVEC TON GROUPE.\nMAIS C\u2019EST TOI QUI CONDUIS TA MOTO.',
    },
  ],
};
